import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Layout } from "../components/Layout";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { 
  Check, 
  Sparkles, 
  CreditCard,
  Shield,
  Star,
  Users,
  Loader2
} from "lucide-react";

// PayPal icon component
const PayPalIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .757-.629h6.724c2.332 0 4.177.643 5.244 1.825.474.525.82 1.118 1.02 1.76.218.7.263 1.498.13 2.368-.022.15-.05.303-.084.46-.563 2.688-2.37 4.548-5.436 4.548H9.802l-.733 4.525-.03.148-.456 2.612zm7.267-13.4c-.04.248-.087.5-.142.756-.472 2.148-2.059 2.9-4.12 2.9H8.335l-.97 6.127h2.474l.738-4.613.024-.148.456-2.612h.748c3.066 0 5.095-1.517 5.538-4.41z"/>
  </svg>
);

const PricingPage = () => {
  const { user, token, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("stripe"); // stripe or paypal

  // Handle PayPal return
  useEffect(() => {
    const provider = searchParams.get("provider");
    const orderId = searchParams.get("token"); // PayPal uses 'token' param
    
    if (provider === "paypal" && orderId) {
      // Capture the PayPal payment
      const capturePayment = async () => {
        try {
          const response = await axios.post(
            `${API}/subscription/paypal/capture/${orderId}`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          if (response.data.status === "success") {
            toast.success("Paiement PayPal confirmé !");
            await refreshUser();
            navigate("/dashboard");
          } else {
            toast.error("Échec du paiement PayPal");
          }
        } catch (error) {
          toast.error("Erreur lors de la confirmation PayPal");
        }
      };
      
      capturePayment();
    }
  }, [searchParams, token, navigate, refreshUser]);

  const fetchPlans = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/subscription/plans`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlans(response.data.plans);
      setTotalUsers(response.data.total_users);
    } catch (error) {
      console.error("Error fetching plans:", error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) {
      fetchPlans();
    }
  }, [token, fetchPlans]);

  const handleSubscribe = useCallback(async (planId) => {
    setProcessingPlan(planId);
    
    try {
      const endpoint = paymentMethod === "paypal" 
        ? `${API}/subscription/paypal/checkout`
        : `${API}/subscription/checkout`;
        
      const response = await axios.post(
        endpoint,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.status === "success") {
        // Free plan activated
        toast.success(response.data.message);
        navigate("/dashboard");
      } else if (response.data.url) {
        // Redirect to payment provider
        window.location.href = response.data.url;
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Erreur lors du paiement";
      toast.error(errorMsg);
      
      // If PayPal not configured, suggest Stripe
      if (errorMsg.includes("PayPal non configuré")) {
        setPaymentMethod("stripe");
        toast.info("Basculement vers paiement par carte");
      }
    } finally {
      setProcessingPlan(null);
    }
  }, [token, paymentMethod, refreshUser, navigate]);

  const planIcons = {
    pioneer: Sparkles,
    standard: Shield
  };

  const planColors = {
    pioneer: "border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50",
    standard: "border-pink-200 bg-gradient-to-br from-pink-50 to-rose-50"
  };

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center animate-fadeIn">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 rounded-full text-amber-700 font-bold text-sm mb-4">
            <Sparkles size={16} />
            Offre de lancement
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
            Choisissez votre plan
          </h1>
          <p className="text-slate-600">
            Plus vous rejoignez tôt, plus vous économisez !
          </p>
          <p className="text-sm text-slate-500 mt-2">
            <Users size={14} className="inline mr-1" />
            {totalUsers} membres inscrits
          </p>
        </div>

        {/* Payment Methods Selector */}
        <div className="max-w-md mx-auto">
          <p className="text-center text-sm text-slate-500 mb-3">Choisissez votre méthode de paiement</p>
          <div className="flex gap-3">
            <button
              onClick={() => setPaymentMethod("stripe")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                paymentMethod === "stripe"
                  ? "border-[#FF3366] bg-pink-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <CreditCard size={20} className={paymentMethod === "stripe" ? "text-[#FF3366]" : "text-slate-400"} />
              <span className={`font-medium ${paymentMethod === "stripe" ? "text-[#FF3366]" : "text-slate-600"}`}>
                Carte bancaire
              </span>
            </button>
            <button
              onClick={() => setPaymentMethod("paypal")}
              className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-xl border-2 transition-all ${
                paymentMethod === "paypal"
                  ? "border-[#0070BA] bg-blue-50"
                  : "border-slate-200 bg-white hover:border-slate-300"
              }`}
            >
              <PayPalIcon className={`w-5 h-5 ${paymentMethod === "paypal" ? "text-[#0070BA]" : "text-slate-400"}`} />
              <span className={`font-medium ${paymentMethod === "paypal" ? "text-[#0070BA]" : "text-slate-600"}`}>
                PayPal
              </span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto animate-fadeIn stagger-1">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Card key={i} className="guardian-card animate-pulse">
                <CardContent className="p-6 h-80" />
              </Card>
            ))
          ) : (
            plans.map((plan) => {
              const Icon = planIcons[plan.id] || Star;
              const isCurrentPlan = user?.subscription_plan === plan.id;
              const isPioneer = plan.id === "pioneer";
              
              return (
                <Card 
                  key={plan.id} 
                  className={`guardian-card relative overflow-hidden ${planColors[plan.id] || ''} ${isPioneer ? 'border-2' : ''}`}
                >
                  {isPioneer && plan.available && (
                    <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-amber-400 to-orange-500 text-white text-center py-1.5 text-xs font-bold">
                      🔥 {plan.spots_left} PLACES RESTANTES
                    </div>
                  )}
                  
                  <CardContent className={`p-6 text-center ${isPioneer && plan.available ? 'pt-10' : ''}`}>
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4 ${
                      isPioneer ? 'bg-amber-100' : 'bg-slate-100'
                    }`}>
                      <Icon size={28} className={isPioneer ? 'text-amber-600' : 'text-slate-600'} />
                    </div>
                    
                    <p className="text-sm font-bold text-slate-500 mb-1">{plan.name}</p>
                    
                    <div className="mb-4">
                      {plan.price === 0 ? (
                        <>
                          <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                            GRATUIT
                          </span>
                          <p className="text-xs text-slate-500">pendant 1 an</p>
                        </>
                      ) : (
                        <>
                          <span className="text-3xl font-extrabold text-slate-900" style={{ fontFamily: 'Outfit' }}>
                            {plan.price}€
                          </span>
                          <span className="text-slate-500">/mois</span>
                        </>
                      )}
                    </div>

                    <ul className="text-sm text-slate-600 space-y-2 mb-6 text-left">
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        Accès complet
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        Badge "{plan.name}"
                      </li>
                      <li className="flex items-center gap-2">
                        <Check size={16} className="text-green-500 flex-shrink-0" />
                        {isPioneer ? 'Support prioritaire' : 'Support standard'}
                      </li>
                    </ul>

                    {isCurrentPlan ? (
                      <Button disabled className="w-full rounded-full bg-green-100 text-green-700">
                        <Check size={16} className="mr-2" />
                        Plan actuel
                      </Button>
                    ) : !plan.available ? (
                      <Button disabled variant="outline" className="w-full rounded-full">
                        Non disponible
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleSubscribe(plan.id)}
                        disabled={processingPlan !== null}
                        className={`w-full rounded-full ${
                          isPioneer 
                            ? 'bg-gradient-to-r from-amber-400 to-orange-500 hover:from-amber-500 hover:to-orange-600 text-white' 
                            : 'btn-guardian'
                        }`}
                      >
                        {processingPlan === plan.id ? (
                          <>
                            <Loader2 size={16} className="mr-2 animate-spin" />
                            Chargement...
                          </>
                        ) : plan.price === 0 ? (
                          "Activer gratuitement"
                        ) : (
                          "S'abonner"
                        )}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Info */}
        <div className="text-center text-sm text-slate-500">
          <p>💳 Paiement sécurisé par Stripe</p>
        </div>
      </div>
    </Layout>
  );
};

export default PricingPage;
